/*
===============================================================================================================
                                 Copyright 2023-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable react/no-array-index-key */
import React, { useContext, useState, useEffect } from 'react';
import { AdminContext } from '@/contexts/admin-context';
import { LocaleContext } from '@/contexts/locale-context';
import { ChangesContext } from '@/contexts/changes-context/changes-context';
import { RoutingContext } from '@/contexts/routing-context';
import { ThemeContext } from '@/contexts/theme-context';
import { Submit, FormError } from '@/contexts/validation-context';
import RadioButtonWrapper from '@/components/radio-button-wrapper';
import generateControls from '@/modules/settings-collection/generate-control';
import Checkbox from '@/components/checkbox';
import { Modal } from '@/components/modal';
import TextDiv from '@/components/text-div';
import LoadingArc from '@/components/loading-arc';
import { replaceTemplatesInString } from '@/utilities/strings';
import getValue, { valueIds } from '@/mappings/signup';
import { useAppAuth } from '@/utilities/authorisation';
import sendSignupEmails from '@/modules/emails/send-signup-emails';
import { useRouter } from 'next/router';
import { getStaticCdnPath } from '@/utilities/static-build';

export default function Signup() {
  const router = useRouter();
  const { translate } = useContext(LocaleContext);
  const { loginAndSignupPageLogoSize } = useContext(ThemeContext);
  const {
    getCarrierLogo, getAdminFeature, getAdminProductName, getCarrierName, getAdminConfigValue, getCarrierSubBrandLogoUrl,
  } = useContext(AdminContext);
  const {
    changes, addChange, publishChanges, error: publishError, publishingState, changeStatus,
  } = useContext(ChangesContext);
  const { showFooter } = useContext(RoutingContext);
  const [{ authData }] = useAppAuth();
  const [attemptPublish, setAttemptPublish] = useState(false);
  const [checkboxValidations, setCheckboxValidations] = useState(null);
  const { signupBccEmailMerchantRequestNotification, signupAdminMerchantRequestEmail, signupCollectionWidgetEmail } = getAdminConfigValue('emailAddresses');
  const pageConfig = getAdminFeature('signup');

  const termsCheckbox = pageConfig?.termsCheckbox;
  const productNames = pageConfig?.productNames;
  const backgroundColor = pageConfig?.backgroundColor || 'var(--color-grey)';

  const onChange = ({ updateType, updateKey, valueId }) => (newValue) => {
    addChange({
      type: updateType,
      key: updateKey,
      value: newValue.value || newValue,
      valueId,
    });
  };

  const generateControl = generateControls(
    translate,
    getValue({
      translate, changes, productName: getAdminProductName(),
    }),
    onChange,
  );

  const generateAgreementLinkText = (params) => replaceTemplatesInString({
    text: translate(params?.label),
    templates: {
      '<1>': { tag: 'a', href: params?.termsLink },
    },
    className: 'global-link--underlined',
  });

  const generateAgreementLinkTextTwo = (params) => replaceTemplatesInString({
    text: translate(params?.labelTwo),
    templates: {
      '<2>': { tag: 'a', href: params?.privacyPolicy },
    },
    className: 'global-link--underlined',
  });

  const generateSuccessText = (widget, product) => {
    if (widget && product) {
      return translate('signup.collectionsWidgetBothMessage').replace('{widgetName}', widget).replace('{productName}', product);
    }
    if (widget) {
      return translate('signup.collectionsWidgetOnlyMessage').replace('{widgetName}', widget);
    }
    if (product) {
      return translate('signup.productOnlyMessage').replace('{productName}', product);
    }
    return null;
  };

  const checkboxBooleans = changes.map(((change) => !!valueIds[change.valueId]));
  const checkboxValues = changes.map(((change) => valueIds[change.valueId]));

  // Text to be shown on successful signup
  const hasPortal = checkboxValues.includes(valueIds.DOMESTIC_PORTAL);
  const hasInternational = checkboxValues.includes(valueIds.INTERNATIONAL_PORTAL);
  const hasWidget = checkboxValues.includes(valueIds.COLLECTION_WIDGET);

  const getPortalProductName = () => {
    const names = [
      hasPortal ? productNames?.product : null,
      hasInternational ? productNames?.international : null,
    ].filter(Boolean);
    return names.length > 0 ? names.join(' & ') : undefined;
  };

  // Check to see if the checkbox changes contains correct values for validation
  const checkboxValidation = () => (checkboxBooleans.includes(true) ? String(checkboxBooleans.includes(true)) : null);

  const showSuccessModal = !publishError?.error && attemptPublish && publishingState === changeStatus.PUBLISHED;
  const showContentCard = !attemptPublish && publishingState !== changeStatus.PUBLISHING;
  const showLoading = !showSuccessModal && !showContentCard;

  useEffect(() => {
    if (publishError?.error) {
      setAttemptPublish(false);
    }
  }, [publishError]);

  useEffect(() => {
    if (changes) {
      setCheckboxValidations(checkboxValidation());
    }
  }, [changes]);

  useEffect(() => {
    if (showSuccessModal) {
      sendSignupEmails({
        tokenType: authData?.token_type,
        accessToken: authData?.access_token,
        getCarrierName,
        data: changes,
        emailsToSend: pageConfig?.emailsToSend,
        adminEmail: signupBccEmailMerchantRequestNotification,
        adminNotificationEmail: signupAdminMerchantRequestEmail,
        collectionWidgetEmail: signupCollectionWidgetEmail,
      });
    }
  }, [showSuccessModal]);

  if (router.query.shopify === '' || router.query.shopify === 'true') {
    router.push(`signup/your-business${router.query.company ? `?company=${router.query.company}` : ''}`);
    return <></>;
  }
  return (
    <div className="main">
      <div className="outer">
        <div>
          {showSuccessModal && (
            <Modal
              selectorId="signup-success-modal"
              closeEnabled={false}
              useClickaway={false}
              containerWidth="35%"
              onClose={() => {}}
            >
              <div className="card-notification-modal">
                {pageConfig?.checkboxes?.length > 0 && (
                  <TextDiv selectorId="checkbox-product-text">
                    {generateSuccessText(hasWidget ? productNames?.widget : undefined, getPortalProductName())}
                  </TextDiv>
                )}
                <TextDiv selectorId="next-steps-text">
                  {replaceTemplatesInString(
                    {
                      text: translate('wl.signup.successMessage'),
                      templates: {
                        '<br/>': <br />,
                      },
                    },
                  )}
                </TextDiv>
              </div>
            </Modal>
          )}
        </div>
        {showLoading && (
          <Modal
            selectorId="signup-loading-modal"
            closeEnabled={false}
            useClickaway={false}
            containerWidth="35%"
            onClose={() => {}}
          >
            <div className="card-notification-modal">
              <LoadingArc />
            </div>
          </Modal>
        )}
        {showContentCard && (
        <div className="content-card">
          <div className="logo-wrapper">
            {!getCarrierSubBrandLogoUrl()
              ? (getCarrierLogo() && <img className="carrier-logo" alt="carrier-logo" src={getCarrierLogo()} />)
              : (
                <div className="carrier-group-logos">
                  {getCarrierLogo() && <img className="carrier-logo" alt="carrier-logo" src={getCarrierLogo()} />}
                  {getCarrierSubBrandLogoUrl() && <img className="carrier-logo" alt="carrier-logo" src={getCarrierSubBrandLogoUrl()} />}
                </div>
              )}
          </div>
          <div className="heading">
            <div className="title">
              {translate('wl.signup.title')}
            </div>
            <div className="sub-title">
              {translate('wl.signup.subTitle')}
            </div>
          </div>
          {pageConfig?.checkboxes?.length > 0
          && (
          <div className="checkbox-wrapper">
            <div className="checkbox-heading">
              {translate('signup.checkboxTitle')}
            </div>
            <RadioButtonWrapper
              inputSelectorId="signup-radio-buttons"
              selectedItemId={checkboxValidations}
              defaultValue={checkboxValidations}
              validationRules={{
                onChange: [],
                onSubmit: [],
                requiredError: translate('signup.radioButtonRequiredError'),
                onChangeErrors: [],
                submitErrors: ['signup.radioButtonRequiredError'],
              }}
              required
            >
              {pageConfig.checkboxes?.map((column, index) => (
                <div className="checkbox-column" key={`checkbox-column-${index}`}>
                  {column.map((row, idx) => (
                    <div className="checkbox-column-row" key={`checkbox-column-row-${index}-${idx}`}>
                      {generateControl(row, generateControl)}
                    </div>
                  ))}
                </div>
              ))}
            </RadioButtonWrapper>
          </div>
          )}
          <div className="form-wrapper">
            {pageConfig?.details?.length > 0 && pageConfig.details?.map((row, index) => (
              <div className="details-row" key={`details-row-${index}`}>
                {row.map((field, idx) => (
                  <div key={`details-column-field-${index}-${idx}`}>
                    {generateControl(field, generateControl)}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {termsCheckbox?.length > 0 && (
          <div className="terms-checkbox">
            {termsCheckbox.map((checkbox) => (
              <Checkbox
                key={checkbox.id}
                inputSelectorId={checkbox.id}
                required={checkbox.params?.required}
                onChange={(value) => {
                  addChange({
                    type: checkbox.params?.updateType,
                    key: checkbox.params?.updateValue,
                    value: value.value || value,
                    valueId: checkbox.params?.valueId,
                  });
                }}
                validationRules={{
                  onChange: [],
                  onSubmit: [],
                  requiredError: translate(checkbox.params?.requiredError),
                  onChangeErrors: [],
                  submitErrors: [],
                }}
              >
                <>
                  {checkbox.params?.termsLink && generateAgreementLinkText(checkbox.params)}
                  {checkbox.params?.privacyPolicy && generateAgreementLinkTextTwo(checkbox.params)}
                </>
              </Checkbox>
            ))}
          </div>
          )}
          {publishError?.message && (
            <div className="api-error-container">
              {translate(publishError?.message)}
            </div>
          )}
          <div className="button-wrapper">
            {pageConfig?.emptyFormValidation && (
              <div className="form-error-container">
                <FormError showOnlyRequiredError />
              </div>
            )}
            <Submit
              inputSelectorId="signup-button"
              buttonLabel={translate('signup.submit')}
              onSubmitFunction={() => {
                publishChanges({ appToken: authData, clearState: false, updateDelay: 0 });
                setAttemptPublish(true);
              }}
            />
          </div>
        </div>
        )}
      </div>
      <style jsx>
        {`
        
          .main {
            background: ${pageConfig.noBackgroundImage ? backgroundColor : 'transparent'};
            isolation: isolate;
            position: relative;
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            width: 100%;
          }

          .main::before {
            content: '';
            position: fixed;
            inset: 0;
            background: ${pageConfig.noBackgroundImage ? 'none' : `url("${getStaticCdnPath()}/admin-background.jpg") center / cover no-repeat`};
            z-index: -1;
          }

          .outer {
            align-items: center;
            display: flex;
            flex-grow: 0.3;
            justify-content: center;
            width: 100%;
            min-height: 100vh;
          }

          .content-card {
            background-color: var(--color-white);
            border-radius: var(--border-radius);
            padding: 30px 50px;
            max-width: 860px;
            min-width: 400px;
            margin-top: 30px;
            margin-bottom: 70px;
          }

          .title {
            font-size: var(--font-size-H1);
            font-weight: var(--font-weight-H1);
            margin-bottom: 30px;
            margin-top: 20px;
            text-align: left;
          }

          .logo-wrapper {
            display: flex;
            justify-content: ${pageConfig?.logoAlignment || 'flex-end'};
            margin-bottom: 20px;
          }
          
          .carrier-logo {
            max-height: ${loginAndSignupPageLogoSize.height}px;
            max-width: ${loginAndSignupPageLogoSize.width}px;
            position: relative;
          }

          .carrier-group-logos {
            display: flex;
          }

          .carrier-group-logos img {
            padding: 10px;
            margin-right: 5px;
          }

          .carrier-group-logos img:first-child {
            border-right: 1px solid var(--color-line-grey);
          }

          .form-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 100%;
          }

          .checkbox-column {
            align-items: flex-start;
            display: flex;
            flex-basis: 45%;
            flex-direction: column;
            justify-content: flex-start;
          }

          .checkbox-column-row {
            width: 100%;
          }
          
          .details-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            column-gap: 15px;
            align-items: flex-end;
          }

          .checkbox-heading {
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H1);
            margin-bottom: 30px;
            text-align: left;
          }
          
          .checkbox-wrapper {
            border-bottom: 1px solid rgb(193, 193, 193);
            margin-bottom: 20px;
          }

          .card-notification-modal {
            display: flex;
            flex-direction: column;
            font-size: var( --font-size-H1);
            font-weight: var(--font-weight-H1);
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 50px;
            min-height: 20vh;
            gap: 20px;
          }

          .card-notification-modal > div {
            margin-bottom: 10px;
          }

          .heading {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            margin-bottom: 20px;
          }

          .button-wrapper {
            align-items: center;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-top: 10px;
          }

          .terms-checkbox {
            padding: 5px;
          }

          .form-error-container {
            color: var(--color-error-red);
          }
          
          .api-error-container {
            color: var(--color-error-red);
            margin-top: 20px;
            font-size: var(--font-size-error);
          }

          @media only screen and (max-width: 1100px) {

              .form-wrapper {
                flex-direction: column;
                gap: 0px;
              }
              .content-card {
                min-width: initial;
                max-width: 100%;
                margin: 0;
              }

              .details-row {
                grid-template-columns: 1fr;
              }

              .outer {
                margin-bottom: ${showFooter() ? 50 : 10}px;
                width: auto;
              }

              .main {
                background: ${showContentCard && 'none'}
              }

          }

          @media only screen and (max-width: 768px) {
            .logo-wrapper {
              justify-content: center;
            }

            :global(#terms-checkbox) {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
            }
          }
        `}
      </style>
    </div>
  );
}
